import { useEffect, useState, useCallback, useRef } from 'react';
import type { StepContentProps } from '~/ui-rocco/multi-step-form';
import { useUserLoggedIn } from '@modules/user';
import { useTracker } from '@archipro-website/tracker';
import { useLocation } from '@remix-run/react';
import {
    AUTH_PROVIDER_PARAM_KEY,
    AUTH_SOURCE_PARAM_KEY,
} from '~/modules/root/hook/use-auth-event';
import { ReactComponent as Facebook } from '@rocco/icons/svg/brand/facebook.svg';
import { ReactComponent as Google } from '@rocco/icons/svg/brand/google.svg';
import { ReactComponent as Linkedin } from '@rocco/icons/svg/brand/linkedin.svg';
import { cn } from '@rocco/utils/cn';
import { useAppDisplayModeContext } from '@archipro-design/aria';

import LoginSignUpForm from '~/modules/user/component/login-signup-form/LoginSignUpForm';
import ForgotPasswordForm from '~/modules/user/component/forgot-password/ForgotPasswordForm';
import { StepContent } from '../StepContent';
import { FormHeader } from '~/ui-rocco/multi-step-form/FormHeader';
import { usePublicUser } from '~/modules/user/hook/use-public-user';
import { useDataRefresh } from '~/modules/root/hook/use-data-refresh';
import type { DetailedPlace } from '@modules-rocco/location/type/detailed-place';
import { HdeRegistrationMarketingConsent } from '../HdeRegistrationMarketingConsent';
import { HDE_MARKETING_CONSENT_ERROR_MESSAGE } from '~/modules/hde/constants';

/** Email/password flow from public-user lookup; pro/exhibitor HDE auth is login-only (`login`). */
type HdeAuthEffectiveFlow = 'login' | 'sign-up' | null;

interface SocialButtonProps {
    provider: 'Facebook' | 'Google' | 'LinkedIn';
    icon: React.ReactNode;
    href: string;
    label: string;
    className?: string;
    /** When true, click is blocked: navigation prevented and onBlockedClick runs. */
    blocked?: boolean;
    onBlockedClick?: () => void;
}

/**
 * Social login button component - white pill with icon and text label.
 */
function SocialButton({
    provider,
    icon,
    label,
    href,
    className,
    blocked = false,
    onBlockedClick,
}: SocialButtonProps) {
    const pillClass = cn(
        'flex h-11 items-center gap-2 whitespace-nowrap rounded-[40px] bg-white py-3 pl-5 pr-[1.875rem]',
        className,
    );

    const handleClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
        if (blocked) {
            event.preventDefault();
            onBlockedClick?.();
        }
    };

    const handleKeyDown = (event: React.KeyboardEvent<HTMLAnchorElement>) => {
        if (blocked && (event.key === 'Enter' || event.key === ' ')) {
            event.preventDefault();
            onBlockedClick?.();
        }
    };

    return (
        <a
            href={href}
            onClick={handleClick}
            onKeyDown={handleKeyDown}
            className={pillClass}
            aria-label={`Continue with ${provider}`}
            aria-disabled={blocked ? true : undefined}
        >
            <span className="flex size-4 shrink-0 items-center justify-center">
                {icon}
            </span>
            <span className="text-base font-medium text-black">{label}</span>
        </a>
    );
}

function hasMeaningfulFormData(data: HDEFormData | undefined): boolean {
    if (!data) return false;
    if ('applicationType' in data) {
        const p = data as HDEExhibitorFormData | HDEProfessionalFormData;
        return !!(
            p.isProfessionalMember === true ||
            p.eventId ||
            p.companyName ||
            p.companyEmail ||
            p.companyWebsite ||
            p.phone
        );
    }
    const d = data as HDEHomeownerFormData;
    return !!(
        d.eventId ||
        (d.attendanceReasons?.length ?? 0) > 0 ||
        d.otherReason ||
        (d.productInterests?.length ?? 0) > 0 ||
        d.projectStage ||
        d.projectType ||
        d.projectScope ||
        d.projectLocation ||
        d.budget ||
        d.bringGuest ||
        d.guestName ||
        d.guestEmail ||
        d.selectedLocation
    );
}

type HDEModalType = 'homeowner' | 'professional' | 'exhibitor';

function getModalTypeFromFormData(hdeFormData?: HDEFormData): HDEModalType {
    if (!hdeFormData || !('applicationType' in hdeFormData)) return 'homeowner';
    if (hdeFormData.applicationType === 'Exhibitor') return 'exhibitor';
    if (hdeFormData.applicationType === 'Professional') return 'professional';
    return 'homeowner';
}

function buildSocialLoginUrl(
    provider: string,
    pathname: string,
    hdeFormData?: HDEFormData,
): string {
    const modalType = getModalTypeFromFormData(hdeFormData);
    const params = new URLSearchParams();
    params.set(AUTH_PROVIDER_PARAM_KEY, provider);
    params.set(AUTH_SOURCE_PARAM_KEY, 'HDE');
    params.set('redirect', encodeURI(`${pathname}?hde-modal=${modalType}`));
    params.set('skip-post-sign-up', '1');
    params.set('mark-post-sign-up-seen', '1');

    if (hdeFormData && hasMeaningfulFormData(hdeFormData)) {
        params.set('hde-form-data', serializeHDEFormData(hdeFormData));
    }

    return `/member/social?${params.toString()}`;
}

/** Homeowner form data (project-focused fields) */
interface HDEHomeownerFormData {
    eventId: string | null;
    primaryEventId?: string | null;
    attendanceReasons: Array<{ value: string }>;
    otherReason: string;
    productInterests: Array<{ value: string }>;
    otherProductInterest?: string;
    projectStage: string | null;
    projectType: string | null;
    projectScope: string | null;
    projectLocation: string;
    selectedLocation: DetailedPlace | null;
    budget: string | null;
    bringGuest: boolean;
    guestName: string;
    guestEmail: string;
}

/** Exhibitor form data passed to auth step for social login persistence */
interface HDEExhibitorFormData {
    applicationType: 'Exhibitor';
    isProfessionalMember: boolean | null;
    selectedProfessionalId: string | null;
    eventId: string | null;
    primaryEventId?: string | null;
    companyName: string;
    companyWebsite: string;
    companyEmail: string;
    phone: string;
    companyType: string | null;
}

/** Professional form data passed to auth step for social login persistence */
interface HDEProfessionalFormData {
    applicationType: 'Professional';
    isProfessionalMember: boolean | null;
    selectedProfessionalId: string | null;
    eventId: string | null;
    primaryEventId?: string | null;
    companyName: string;
    companyWebsite: string;
    companyEmail: string;
    phone: string;
    companyType: string | null;
    bringGuest: boolean | null;
    guestName: string;
    guestEmail: string;
}

/** Union of all HDE form data types passed to the auth step */
type HDEFormData =
    | HDEHomeownerFormData
    | HDEExhibitorFormData
    | HDEProfessionalFormData;

function parseEventIds(eventId: string | null): number[] {
    if (!eventId) return [];
    return eventId.includes(',')
        ? eventId
              .split(',')
              .map((id) => Number(id.trim()))
              .filter((id) => !isNaN(id))
        : [Number(eventId)];
}

function serializeHDEHomeownerFormData(data: HDEHomeownerFormData): string {
    const eventIds = parseEventIds(data.eventId);
    const serialized: Record<string, unknown> = {
        ApplicationType: 'Homeowner',
        EventIDs: eventIds,
        ...(data.primaryEventId && { PrimaryEventID: data.primaryEventId }),
        ProjectAttendanceReason: JSON.stringify(
            data.attendanceReasons.map((r) => r.value),
        ),
        HDEOtherPleaseSpecify: data.otherReason,
        ProjectProductInterest: JSON.stringify(
            data.productInterests.map((p) =>
                p.value === 'Other'
                    ? (data.otherProductInterest ?? '')
                    : p.value,
            ),
        ),
        ProjectStage: data.projectStage,
        ProjectType: data.projectType,
        ProjectScope: data.projectScope,
        ProjectLocation: data.projectLocation,
        ProjectBudget: data.budget,
        BringingGuest: data.bringGuest,
        GuestEmail: data.guestEmail,
    };

    if (data.selectedLocation) {
        serialized.HDEBuildHubLocationData = JSON.stringify(
            data.selectedLocation,
        );
    }

    if (data.guestName) {
        const nameParts = data.guestName.trim().split(/\s+/).filter(Boolean);
        const firstName = nameParts[0] || '';
        const lastName = nameParts.slice(1).join(' ').trim() || firstName;
        if (firstName) serialized.GuestFirstName = firstName;
        if (lastName) serialized.GuestLastName = lastName;
    }

    return JSON.stringify(serialized);
}

function serializeHDEExhibitorFormData(data: HDEExhibitorFormData): string {
    const eventIds = parseEventIds(data.eventId);
    const serialized: Record<string, unknown> = {
        ApplicationType: 'Exhibitor',
        IsProfessionalMember: data.isProfessionalMember,
        SelectedProfessionalId: data.selectedProfessionalId,
        EventIDs: eventIds,
        ...(data.primaryEventId && { PrimaryEventID: data.primaryEventId }),
        CompanyName: data.companyName || '',
        CompanyWebsite: data.companyWebsite || '',
        Email: data.companyEmail || '',
        Phone: data.phone || '',
        CompanyType: data.companyType || '',
        BringingGuest: false,
    };
    return JSON.stringify(serialized);
}

function serializeHDEProfessionalFormData(
    data: HDEProfessionalFormData,
): string {
    const eventIds = parseEventIds(data.eventId);
    const serialized: Record<string, unknown> = {
        ApplicationType: 'Professional',
        IsProfessionalMember: data.isProfessionalMember,
        SelectedProfessionalId: data.selectedProfessionalId,
        EventIDs: eventIds,
        ...(data.primaryEventId && { PrimaryEventID: data.primaryEventId }),
        CompanyName: data.companyName || '',
        CompanyWebsite: data.companyWebsite || '',
        Email: data.companyEmail || '',
        Phone: data.phone || '',
        CompanyType: data.companyType || '',
        BringingGuest: data.bringGuest === true,
        GuestEmail: data.guestEmail || '',
    };

    if (data.guestName) {
        const nameParts = data.guestName.trim().split(/\s+/).filter(Boolean);
        const firstName = nameParts[0] || '';
        const lastName = nameParts.slice(1).join(' ').trim() || firstName;
        if (firstName) serialized.GuestFirstName = firstName;
        if (lastName) serialized.GuestLastName = lastName;
    }

    return JSON.stringify(serialized);
}

function serializeHDEFormData(data: HDEFormData): string {
    if ('applicationType' in data) {
        if (data.applicationType === 'Exhibitor') {
            return serializeHDEExhibitorFormData(data);
        }
        if (data.applicationType === 'Professional') {
            return serializeHDEProfessionalFormData(data);
        }
    }
    return serializeHDEHomeownerFormData(data as HDEHomeownerFormData);
}

export function HDEAuthenticationStep({
    setIsValid,
    onDataChange,
    initialData,
}: StepContentProps) {
    const { mobile } = useAppDisplayModeContext();
    const { isLoggedIn } = useUserLoggedIn();
    const [showEmailForm, setShowEmailForm] = useState(true);
    const [forgotPasswordCompleted, setForgotPasswordCompleted] =
        useState(false);
    const [showAuthLoading, setShowAuthLoading] = useState(false);
    /** Homeowner-only: marketing consent for HDE registration (default unchecked). */
    const [hdeMarketingConsentChecked, setHdeMarketingConsentChecked] =
        useState(false);
    /** Show inline validation error under consent checkbox after a blocked attempt. */
    const [showConsentError, setShowConsentError] = useState(false);
    const { refresh } = useDataRefresh();

    // Report auth loading state to parent so header can be hidden
    useEffect(() => {
        onDataChange?.({ _authLoading: showAuthLoading });
    }, [showAuthLoading, onDataChange]);
    const { pathname } = useLocation();

    const { publicUser, setPublicUser, handleUserExists, publicUserLoginFlow } =
        usePublicUser('HDE');

    const initData = initialData as {
        hdeFormData?: HDEFormData;
        showForgotPassword?: boolean;
        parentShowEmailForm?: boolean;
        onBack?: () => void;
    };
    const hdeFormData = initData?.hdeFormData;
    const showForgotPassword = initData?.showForgotPassword ?? false;
    const parentShowEmailForm = initData?.parentShowEmailForm;
    const onBack = initData?.onBack;

    const modalType = getModalTypeFromFormData(hdeFormData);
    const isNonHomeowner =
        modalType === 'professional' || modalType === 'exhibitor';
    const effectiveFlow: HdeAuthEffectiveFlow = isNonHomeowner
        ? 'login'
        : (publicUserLoginFlow ?? null);
    const companyEmail =
        hdeFormData && 'companyEmail' in hdeFormData
            ? (hdeFormData as HDEExhibitorFormData | HDEProfessionalFormData)
                  .companyEmail
            : undefined;

    const facebookUrl = buildSocialLoginUrl('Facebook', pathname, hdeFormData);
    const googleUrl = buildSocialLoginUrl('Google', pathname, hdeFormData);
    const linkedinUrl = buildSocialLoginUrl('LinkedIn', pathname, hdeFormData);
    const isPasswordVisible = effectiveFlow === 'login';
    /** Social login is homeowner-only. Pros/exhibitors use email/password; homeowners hide it once the password step is shown. */
    const showHdeSocialLoginRow =
        !isNonHomeowner && effectiveFlow !== 'sign-up' && !isPasswordVisible;
    const hdeAuthSubmissionAllowed =
        isNonHomeowner || hdeMarketingConsentChecked;
    const socialAuthBlocked = !isNonHomeowner && !hdeMarketingConsentChecked;

    const handleHdeMarketingConsentChange = useCallback(
        (checked: boolean) => {
            setHdeMarketingConsentChecked(checked);
            if (checked) {
                setShowConsentError(false);
            } else if (effectiveFlow !== 'sign-up') {
                setPublicUser(undefined);
            }
        },
        [effectiveFlow, setPublicUser],
    );

    const showConsentErrorMessage = useCallback(() => {
        setShowConsentError(true);
    }, []);

    /** Do not run public-user lookup (login vs sign-up) until marketing consent is checked. */
    const handleHomeownerEmailBlur = useCallback(
        (email: string) => {
            if (!hdeMarketingConsentChecked) {
                if (email.trim()) {
                    setShowConsentError(true);
                }
                return;
            }
            handleUserExists(email);
        },
        [hdeMarketingConsentChecked, handleUserExists],
    );

    /** Homeowner-only consent UI; reused in email form slot and compact social view. */
    const consentErrorText =
        !hdeMarketingConsentChecked && showConsentError
            ? HDE_MARKETING_CONSENT_ERROR_MESSAGE
            : undefined;
    const hdeSubmissionConsentSlot = !isNonHomeowner ? (
        <HdeRegistrationMarketingConsent
            id="hde-marketing-consent"
            checked={hdeMarketingConsentChecked}
            onCheckedChange={handleHdeMarketingConsentChange}
            errorMessage={consentErrorText}
        />
    ) : null;

    const handleAuthSuccess = useCallback(() => {
        refresh();
    }, [refresh]);

    /** Auth step validity is not tied to consent; blocked attempts show a toast instead. */
    useEffect(() => {
        setIsValid(true);
    }, [setIsValid]);

    useEffect(() => {
        onDataChange?.({ showEmailForm });
    }, [showEmailForm, onDataChange]);

    // Sync local state when parent explicitly resets (true → false transition only)
    const prevParentShowEmailForm = useRef(parentShowEmailForm);
    useEffect(() => {
        if (
            prevParentShowEmailForm.current === true &&
            parentShowEmailForm === false
        ) {
            setShowEmailForm(false);
            setForgotPasswordCompleted(false);
        }
        prevParentShowEmailForm.current = parentShowEmailForm;
    }, [parentShowEmailForm]);

    const tracker = useTracker();
    useEffect(() => {
        if (isLoggedIn) return;
        tracker.log('AuthModalImpression', {
            url: new URL(window.location.href),
            data: { Source: 'HDE' },
            ga4Data: { source: 'HDE' },
            targetTracker: ['archiproTracker', 'ga4Tracker'],
        });
    }, [tracker, isLoggedIn]);

    if (isLoggedIn) {
        return null;
    }

    return (
        <div className="scrollbar-hide font-sf-pro relative flex w-full flex-col overflow-y-auto">
            <div className="absolute left-0 top-0">Almost there!</div>
            <StepContent className="w-full flex-none pb-12 md:pb-0">
                {!showForgotPassword && (
                    <FormHeader
                        variant="hde"
                        title={
                            modalType === 'homeowner'
                                ? 'Create your ArchiPro account or log in'
                                : 'Please log in to your business account'
                        }
                        description={
                            modalType === 'homeowner'
                                ? 'This gives you your HDE ticket, plus full access to design boards, product research, professional connections, and a personalised project brief all free.'
                                : 'Full access to design boards, product research, professional connections, and a personalised project brief — all free.'
                        }
                        className={
                            modalType === 'homeowner' &&
                            effectiveFlow === 'sign-up'
                                ? '-mt-8 md:mt-0'
                                : undefined
                        }
                    />
                )}
                {showForgotPassword ? (
                    <ForgotPasswordForm
                        variant="hde"
                        flow={
                            forgotPasswordCompleted
                                ? 'forgot-password-completed'
                                : 'forgot-password'
                        }
                        onBack={() => {
                            setForgotPasswordCompleted(false);
                            onDataChange?.({ showForgotPassword: false });
                        }}
                        onSuccess={() => setForgotPasswordCompleted(true)}
                    />
                ) : (
                    <div className="flex w-full flex-col">
                        <div className="mt-4 flex flex-col gap-2 md:mt-4 md:pb-16">
                            {showHdeSocialLoginRow ? (
                                <>
                                    <div className="flex w-full flex-col items-stretch justify-center gap-1.5 md:items-center">
                                        <SocialButton
                                            provider="Google"
                                            icon={<Google className="size-4" />}
                                            label="Google"
                                            href={googleUrl}
                                            className="w-full justify-center md:w-81"
                                            blocked={socialAuthBlocked}
                                            onBlockedClick={
                                                showConsentErrorMessage
                                            }
                                        />
                                        <SocialButton
                                            provider="LinkedIn"
                                            icon={
                                                <Linkedin className="size-4 fill-[#0A66C2]" />
                                            }
                                            label="LinkedIn"
                                            href={linkedinUrl}
                                            className="w-full justify-center md:w-81"
                                            blocked={socialAuthBlocked}
                                            onBlockedClick={
                                                showConsentErrorMessage
                                            }
                                        />
                                        <SocialButton
                                            provider="Facebook"
                                            icon={
                                                <Facebook className="size-4 fill-[#1877F2]" />
                                            }
                                            label="Facebook"
                                            href={facebookUrl}
                                            className="w-full justify-center md:w-81"
                                            blocked={socialAuthBlocked}
                                            onBlockedClick={
                                                showConsentErrorMessage
                                            }
                                        />
                                    </div>
                                    <div className="flex items-center justify-center md:mt-4">
                                        <span className="text-3 text-[#858585]">
                                            Or continue with:
                                        </span>
                                    </div>
                                </>
                            ) : null}
                            <LoginSignUpForm
                                flow={effectiveFlow}
                                authSource="HDE"
                                skipPostSignUp={true}
                                markPostSignUpSeen={true}
                                skipAuthRedirect={true}
                                onSuccess={handleAuthSuccess}
                                allowExternalUrls={false}
                                defaultPublicUser={
                                    isNonHomeowner ? undefined : publicUser
                                }
                                defaultEmail={
                                    isNonHomeowner ? companyEmail : undefined
                                }
                                emailLabel={
                                    isNonHomeowner
                                        ? 'Your company email'
                                        : undefined
                                }
                                onEmailBlur={
                                    isNonHomeowner
                                        ? undefined
                                        : handleHomeownerEmailBlur
                                }
                                variant="hde"
                                onBack={onBack}
                                onLoadingChange={setShowAuthLoading}
                                onResetPassword={(e) => {
                                    e.preventDefault();
                                    onDataChange?.({
                                        showForgotPassword: true,
                                    });
                                }}
                                isSubmissionAllowed={hdeAuthSubmissionAllowed}
                                onBlockedSubmitAttempt={showConsentErrorMessage}
                                hdeSubmissionConsentSlot={
                                    hdeSubmissionConsentSlot
                                }
                                hideHdeFooterButtons={!mobile}
                                onAuthFetchersBusyChange={
                                    mobile
                                        ? undefined
                                        : (busy) =>
                                              onDataChange?.({
                                                  _authFetchersBusy: busy,
                                              })
                                }
                            />
                        </div>
                    </div>
                )}
            </StepContent>
        </div>
    );
}
